import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input, OnDestroy } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Genre } from '../../../domain/genre';
import { MultiSelectOption } from '../multi-select/multi-select.component';
import { GenreSelectorService } from './genre-selector.service';

@Component({
  selector: 'osn-cxb-genre-selector',
  templateUrl: './genre-selector.component.html',
  styleUrls: ['./genre-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GenreSelectorComponent),
      multi: true,
    },
    GenreSelectorService,
  ],
})
export class GenreSelectorComponent implements ControlValueAccessor, OnDestroy {
  @Input('data-testid') public testId?: string;
  @Input() public disableMultiSelect = false;
  @Input() public filterGenres = false;

  public genres: MultiSelectOption[];
  public genreSingleSelectOptions: Genre[];
  public genresControl: UntypedFormControl;
  private readonly destroyed$ = new Subject<void>();

  public propagateChange: Function = () => {};
  public propagateTouch: Function = () => {};

  constructor(private readonly genreSelectorService: GenreSelectorService, private readonly chRef: ChangeDetectorRef) {}

  public ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public writeValue(ids: string[] | Genre[] | string | Genre): void {
    const genreProviderObservable$ = this.filterGenres
      ? this.genreSelectorService.getFilteredGenres$(ids as string | Genre)
      : this.genreSelectorService.genres$;
    genreProviderObservable$
      .pipe(
        takeUntil(this.destroyed$),
        filter((genres) => !!genres),
        tap((genres) => {
          this.genres = genres.map((genre) => ({ label: genre.title, id: genre.id }));
          this.genreSingleSelectOptions = genres;
          this.genresControl = new UntypedFormControl(ids);
          this.chRef.detectChanges();
        }),
        switchMap(() => this.genresControl?.valueChanges)
      )
      .subscribe((value) => {
        if (!value) {
          return;
        }
        this.propagateChange?.(value);
      });
  }

  public registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.genresControl.disable();
    } else {
      this.genresControl.enable();
    }
  }
}
